import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ImageUpload from '../../components/ImageUpload';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [venues, setVenues] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    organization_id: '',
    custom_organization: '',
    venue_id: '',
    start_date: '',
    end_date: '',
    status: 'upcoming',
    location: '',
    website_url: '',
    registration_url: '',
    image_url: '',
    is_featured: false,
    event_type: 'in_person',
    virtual_platform: '',
    virtual_url: '',
    virtual_meeting_id: '',
    virtual_passcode: '',
    event_audience: 'general',
    event_category: 'conference'
  });

  useEffect(() => {
    fetchOrganizations();
    fetchVenues();
    fetchSpeakers();
  }, []);

  const fetchOrganizations = async () => {
    const { data } = await supabase.from('organizations').select('*').order('name');
    setOrganizations(data || []);
  };

  const fetchVenues = async () => {
    const { data } = await supabase.from('venues').select('*').order('name');
    setVenues(data || []);
  };

  const fetchSpeakers = async () => {
    const { data } = await supabase.from('speakers').select('*').order('name');
    setSpeakers(data || []);
  };

  const toggleSpeaker = (speakerId) => {
    setSelectedSpeakers(prev => 
      prev.includes(speakerId) 
        ? prev.filter(id => id !== speakerId)
        : [...prev, speakerId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: eventData, error } = await supabase.from('events').insert({
        title: form.title,
        description: form.description,
        organization_id: form.organization_id === 'other' ? null : (form.organization_id || null),
        custom_organization: form.organization_id === 'other' ? form.custom_organization : null,
        venue_id: form.venue_id || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status,
        location: form.location || null,
        website_url: form.website_url || null,
        registration_url: form.registration_url || null,
        image_url: form.image_url || null,
        is_featured: form.is_featured || false,
        event_type: form.event_type || 'in_person',
        virtual_platform: form.event_type !== 'in_person' ? (form.virtual_platform || null) : null,
        virtual_url: form.event_type !== 'in_person' ? (form.virtual_url || null) : null,
        virtual_meeting_id: form.event_type !== 'in_person' ? (form.virtual_meeting_id || null) : null,
        virtual_passcode: form.event_type !== 'in_person' ? (form.virtual_passcode || null) : null,
        event_audience: form.event_audience || 'general',
        event_category: form.event_category || 'conference'
      }).select().single();

      if (error) throw error;

      // Link speakers to event
      if (selectedSpeakers.length > 0 && eventData) {
        const speakerLinks = selectedSpeakers.map(speakerId => ({
          event_id: eventData.id,
          speaker_id: speakerId
        }));
        
        await supabase.from('event_speakers').insert(speakerLinks);
      }

      navigate('/admin/events');
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Event</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add a new conference or event to the system.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="space-y-6">
          {/* Event Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Event Image
            </label>
            <ImageUpload
              bucket="events"
              currentImage={form.image_url}
              onUpload={(url) => setForm({...form, image_url: url})}
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Event Title *
            </label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="e.g., UPCI General Conference 2026"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Describe the event..."
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Event Type *
            </label>
            <select
              value={form.event_type}
              onChange={(e) => setForm({...form, event_type: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            >
              <option value="in_person">In-Person</option>
              <option value="virtual">Virtual/Online</option>
              <option value="hybrid">Hybrid (In-Person + Virtual)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {form.event_type === 'in_person' && 'Physical event at a venue'}
              {form.event_type === 'virtual' && 'Online event only'}
              {form.event_type === 'hybrid' && 'Both in-person and virtual attendance options'}
            </p>
          </div>

          {/* Virtual Event Details */}
          {(form.event_type === 'virtual' || form.event_type === 'hybrid') && (
            <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Virtual Event Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Virtual Platform
                  </label>
                  <select
                    value={form.virtual_platform}
                    onChange={(e) => setForm({...form, virtual_platform: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Platform</option>
                    <option value="Zoom">Zoom</option>
                    <option value="Microsoft Teams">Microsoft Teams</option>
                    <option value="Google Meet">Google Meet</option>
                    <option value="Webex">Webex</option>
                    <option value="YouTube Live">YouTube Live</option>
                    <option value="Facebook Live">Facebook Live</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Virtual Meeting URL
                  </label>
                  <input
                    type="url"
                    value={form.virtual_url}
                    onChange={(e) => setForm({...form, virtual_url: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder="https://zoom.us/j/..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Meeting ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={form.virtual_meeting_id}
                    onChange={(e) => setForm({...form, virtual_meeting_id: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder="123 456 7890"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Passcode (Optional)
                  </label>
                  <input
                    type="text"
                    value={form.virtual_passcode}
                    onChange={(e) => setForm({...form, virtual_passcode: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder="Enter passcode"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Organization & Venue */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization
              </label>
              <select
                value={form.organization_id}
                onChange={(e) => setForm({...form, organization_id: e.target.value, custom_organization: e.target.value === 'other' ? form.custom_organization : ''})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Organization</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
                <option value="other">Other (AIM, Independent, etc.)</option>
              </select>
              {form.organization_id === 'other' && (
                <input
                  type="text"
                  value={form.custom_organization}
                  onChange={(e) => setForm({...form, custom_organization: e.target.value})}
                  placeholder="Enter organization name (e.g., AIM, Independent Ministry)"
                  className="w-full mt-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Venue {form.event_type === 'virtual' && '(Optional for Virtual Events)'}
              </label>
              <select
                value={form.venue_id}
                onChange={(e) => setForm({...form, venue_id: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                disabled={form.event_type === 'virtual'}
              >
                <option value="">{form.event_type === 'virtual' ? 'No Physical Venue' : 'Select Venue'}</option>
                {venues.map(venue => (
                  <option key={venue.id} value={venue.id}>{venue.name} - {venue.city}, {venue.state}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({...form, start_date: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({...form, end_date: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Status, Audience, Category */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({...form, status: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="upcoming">Upcoming</option>
                <option value="pending">Pending Review</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Audience
              </label>
              <select
                value={form.event_audience}
                onChange={(e) => setForm({...form, event_audience: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="general">General/All Ages</option>
                <option value="singles">Singles</option>
                <option value="married">Married Couples</option>
                <option value="youth">Youth</option>
                <option value="children">Children</option>
                <option value="seniors">Seniors</option>
                <option value="ministers">Ministers/Pastors</option>
                <option value="women">Women</option>
                <option value="men">Men</option>
                <option value="families">Families</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Event Category
              </label>
              <select
                value={form.event_category}
                onChange={(e) => setForm({...form, event_category: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="conference">Conference</option>
                <option value="retreat">Retreat</option>
                <option value="camp">Camp</option>
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
                <option value="convention">Convention</option>
                <option value="rally">Rally</option>
                <option value="revival">Revival</option>
                <option value="training">Training</option>
                <option value="fellowship">Fellowship</option>
                <option value="outreach">Outreach</option>
                <option value="missions">Missions</option>
              </select>
            </div>
          </div>

          {/* Speakers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Speakers
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-48 overflow-y-auto">
              {speakers.length === 0 ? (
                <p className="text-sm text-gray-500">No speakers available. Add speakers first.</p>
              ) : (
                <div className="space-y-2">
                  {speakers.map(speaker => (
                    <label key={speaker.id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectedSpeakers.includes(speaker.id)}
                        onChange={() => toggleSpeaker(speaker.id)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          {speaker.image_url ? (
                            <img src={speaker.image_url} alt={speaker.name} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <span className="text-primary text-sm font-medium">{speaker.name?.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{speaker.name}</p>
                          <p className="text-xs text-gray-500">{speaker.organization || speaker.title}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {selectedSpeakers.length > 0 && (
              <p className="mt-2 text-sm text-gray-500">{selectedSpeakers.length} speaker(s) selected</p>
            )}
          </div>

          {/* Location */}
          {form.event_type !== 'virtual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Location (City, State)
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({...form, location: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                placeholder="e.g., St. Louis, MO"
              />
            </div>
          )}

          {/* URLs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Event Website URL
              </label>
              <input
                type="url"
                value={form.website_url}
                onChange={(e) => setForm({...form, website_url: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Registration URL
              </label>
              <input
                type="url"
                value={form.registration_url}
                onChange={(e) => setForm({...form, registration_url: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Featured Event */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_featured"
              checked={form.is_featured}
              onChange={(e) => setForm({...form, is_featured: e.target.checked})}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Featured Event (shows on homepage)
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => navigate('/admin/events')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEventPage;
